import { clearCustomerSession, transformSignedUpCustomer } from "api";
import {
  ILoggedCustomer,
  ISignUpCustomerResponse,
  TabletOperationMode
} from "api/types";
import { AppStateContext, initialAppState, useAppState } from "AppState";
import { CustomerDataContext } from "components/Gates/CustomerDataGate";
import { TabletDataContext } from "components/Gates/TabletDataGate";
import { isUndefined } from "lodash";
import { useContext } from "react";
import { getApolloContext } from "react-apollo";
import { Routes } from "router";
import api from "screens/Onboarding/Onboarding.api";
import { useCustomer } from "screens/Onboarding/Onboarding.hooks";
import { createContainer } from "unstated-next";
import useReactRouter from "use-react-router";

function useOnboarding() {
  // TODO: query for user data
  const {
    customer,
    setters,
    usageTypes,
    interests,
    clearCustomerState
  } = useCustomer();

  const { resetAppState } = useAppState();

  const {
    birthday,
    email,
    firstName,
    interestIds,
    lastName,
    phoneNumber,
    pin,
    usageTypeIds,
    zipcode
  } = customer;
  const loggedCustomer = useContext(CustomerDataContext);
  const tabletData = useContext(TabletDataContext);
  const { client } = useContext(getApolloContext());
  const { history } = useReactRouter();

  const tabletOperationMode = tabletData && tabletData.operationMode;

  const saveCustomerSessionToCache = (c: ILoggedCustomer) => {
    if (isUndefined(client)) {
      throw new Error("Client is not set.");
    }
    api.setCustomerSession(c, client);
  };

  const getCustomerByAuthToken = () => {
    if (isUndefined(loggedCustomer)) {
      throw new Error("Customer data is not set.");
    }
    return api.getCustomerByAuthToken({
      authToken: loggedCustomer.authToken
    });
  };

  // TODO: move to tablet data container:
  /*
  tcpa details
  location
  tablet info
  settings
  */
  const getTcpaDetails = () => {
    return api.getTcpaDetails();
  };

  const getCurrentLocation = () => {
    const {
      currentLocation,
      loadingCurrentLocation,
      error
    } = api.getCurrentLocation();
    if (currentLocation) {
      // setLocationName(currentLocation.name);
    }
  };

  const signUpCustomer = () => {
    if (isUndefined(tabletOperationMode)) {
      throw new Error("Tablet operation mode is not set.");
    }
    return api.signUpCustomer({
      customer: {
        email,
        firstName,
        lastName,
        zipcode,
        birthday,
        phoneNumber,
        pin,
        interestIds,
        usageTypeIds
      },
      tabletOperationMode,
      isMockSignIn: false
    });
  };

  const updateCustomerProfile = () => {
    if (isUndefined(loggedCustomer)) {
      throw new Error("Customer data is not set.");
    }
    return api.updateCustomerProfile({
      customerId: loggedCustomer!.authToken,
      firstName,
      lastName,
      email,
      zipcode,
      birthday
    });
  };

  const goToMainDashboard = () => history.push(Routes.Main);

  const goToHomeScreen = (clear?: boolean) => {
    if (clear) {
      clearCustomerState();
    }
    history.push(Routes.Home);
  };

  const goToKioskWelcome = (newCustomer?: boolean) =>
    newCustomer
      ? history.push(Routes.KioskWelcome)
      : history.push(Routes.KioskWelcomeToLocation);

  const goToCustomerPersonalInfoEditor = () =>
    history.push(Routes.SignUpCustomerPersonalInfo);

  const skipInterestSelectionForCustomer = (
    c: ILoggedCustomer
  ): ILoggedCustomer => {
    return {
      ...c,
      skipInterestsStep: true
    };
  };

  const signInNewCustomer = (
    signUpCustomerResponse: ISignUpCustomerResponse
  ) => {
    const c = transformSignedUpCustomer(signUpCustomerResponse, {
      pin
    });
    // TODO: handle missing loggedCustomer as an exception
    if (c) {
      const loggedCustomerSkippedInterests = skipInterestSelectionForCustomer(
        c
      );
      saveCustomerSessionToCache(loggedCustomerSkippedInterests);
      redirectAfterSignUp(c.newCustomer);
    }
  };

  const logoutCustomer = () => {
    if (isUndefined(client)) {
      throw new Error("Client is not set.");
    }
    clearCustomerSession(client);
    clearCustomerState();
    resetAppState();
  };

  const goTo = ({
    route,
    clearSession = false
  }: {
    route: Routes;
    clearSession?: boolean;
  }) => {
    history.push(route);
    if (clearSession) {
      logoutCustomer();
    }
  };

  const redirectAfterSignUp = (newCustomer?: boolean) =>
    tabletOperationMode === TabletOperationMode.Kiosk
      ? goToKioskWelcome(newCustomer)
      : goToMainDashboard();

  return {
    ...customer,
    ...setters,
    interests,
    usageTypes,
    goToMainDashboard,
    goToHomeScreen,
    goToCustomerPersonalInfoEditor,
    goTo,
    getTcpaDetails,
    signUpCustomer,
    updateCustomerProfile,
    getCurrentLocation,
    logoutCustomer,
    skipInterestSelectionForCustomer,
    signInNewCustomer,
    getCustomerByAuthToken,
    saveCustomerSessionToCache
  };
}

const OnboardingContainer = createContainer(useOnboarding);

export const OnboardingContainerProvider = OnboardingContainer.Provider;
export default OnboardingContainer;



// WEBPACK FOOTER //
// ./src/screens/Onboarding/Onboarding.container.ts